// frontend/src/components/EmailConfirmationHandler.js
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import './EmailConfirmationHandler.css';

const EmailConfirmationHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Check for token in URL (from backend redirect after email confirmation)
    const token = searchParams.get('token');
    const confirmed = searchParams.get('confirmed');
    const alreadyConfirmed = searchParams.get('already_confirmed');
    const error = searchParams.get('error');

    if (token) {
      // Backend sent us a token - store it and fetch user data
      handleAutoLogin(token, confirmed === 'true', alreadyConfirmed === 'true');
    } else if (error) {
      // Backend sent us an error
      handleError(error);
    }
  }, [searchParams]);

  const handleAutoLogin = async (token, isNewlyConfirmed, wasAlreadyConfirmed) => {
    try {
      setStatus('processing');

      if (isNewlyConfirmed) {
        setMessage('Email confirmed! Setting up your account...');
      } else if (wasAlreadyConfirmed) {
        setMessage('Welcome back! Your email was already confirmed.');
      } else {
        setMessage('Logging you in...');
      }

      // Store the token
      authService.setToken(token);

      // Fetch fresh user data
      const userData = await authService.fetchUserFromAPI();

      setStatus('success');

      if (isNewlyConfirmed) {
        setMessage('✅ Email confirmed successfully! Welcome to FocusView!');
      } else if (wasAlreadyConfirmed) {
        setMessage('✅ Welcome back! Your account is already active.');
      } else {
        setMessage('✅ Logged in successfully!');
      }

      // Wait 2 seconds to show success message, then redirect
      setTimeout(() => {
        // Remove query params and reload to update all components
        window.location.href = '/';
      }, 2000);
    } catch (error) {
      console.error('Auto-login error:', error);
      setStatus('error');
      setMessage('Failed to complete login. Please try logging in manually.');

      // Redirect after 3 seconds
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);
    }
  };

  const handleError = (errorType) => {
    setStatus('error');

    switch (errorType) {
      case 'invalid_token':
        setMessage('Invalid or expired confirmation link. Please try signing up again.');
        break;
      case 'confirmation_failed':
        setMessage('Confirmation failed. Please try again or contact support.');
        break;
      default:
        setMessage('An error occurred. Please try again.');
    }

    setTimeout(() => {
      window.location.href = '/';
    }, 3000);
  };

  // Only show modal if we have relevant query params
  const shouldShowModal = searchParams.get('token') || searchParams.get('error');

  if (!shouldShowModal) {
    return null;
  }

  return (
    <div className="confirmation-overlay">
      <div className="confirmation-modal">
        {status === 'processing' && (
          <>
            <div className="spinner"></div>
            <h2>Processing...</h2>
            <p>{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="success-icon">✓</div>
            <h2>Success!</h2>
            <p>{message}</p>
            <p className="redirect-text">Redirecting you to the homepage...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="error-icon">✕</div>
            <h2>Oops!</h2>
            <p>{message}</p>
            <p className="redirect-text">Redirecting you to the homepage...</p>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailConfirmationHandler;